import { memo } from 'react';
import { Alert, Box, Button, SpaceBetween, TextContent } from '@amzn/awsui-components-core';

import { useAuthorDraftStatePanelViewModel } from './AuthorDraftStatePanelViewModel';
import { SaveErrorAlert } from './SaveErrorAlert';
import PanelSection from './PanelSection';
import { PanelDivider } from './PanelDivider';
import { ScanTimeoutAlert } from './ScanTimeoutAlert';

const AuthorDraftStatePanel = memo(function AuthorDraftStatePanel() {
  const vm = useAuthorDraftStatePanelViewModel();

  if (!vm) return null;

  return (
    <SpaceBetween size="m" direction="vertical">
      <PanelSection title="Required to proceed">
        <TextContent>
          <ul>
            {vm.isPatternContent ? (
              <>
                <li>
                  <Box variant="strong" display="inline">
                    Content tab:
                  </Box>{' '}
                  A summary
                </li>
                <li>
                  <Box variant="strong" display="inline">
                    Content tab:
                  </Box>{' '}
                  An epic
                </li>
              </>
            ) : (
              <li>
                <Box variant="strong" display="inline">
                  Content tab:
                </Box>{' '}
                All pages and sections contain text
              </li>
            )}
            <li>
              <Box variant="strong" display="inline">
                Metadata tab:
              </Box>{' '}
              All required fields
            </li>
            <li>
              If you linked to a repo:
              <ul>
                <li>
                  <Box variant="strong" display="inline">
                    Code tab:
                  </Box>{' '}
                  A completed security review ticket
                </li>
                <li>
                  <Box variant="strong" display="inline">
                    Code tab:
                  </Box>{' '}
                  A completed open-source review ticket
                </li>
              </ul>
            </li>
          </ul>
        </TextContent>
      </PanelSection>

      {vm.errorMessage && (
        <SaveErrorAlert
          errorMessage={vm.errorMessage}
          validationIssues={vm.validationIssues}
          onDismiss={vm.dismissError}
        />
      )}

      <SpaceBetween size="xs" direction="vertical">
        <Button
          variant="normal"
          fullWidth
          disabled={vm.isBusy || !vm.isDirty || vm.isSavingDisabled}
          loading={vm.isDiscardLoading}
          onClick={vm.discardChanges}
          data-testid="side-panel-discard-changes"
        >
          Discard all changes
        </Button>
        <Button
          variant="primary"
          fullWidth
          disabled={vm.isQualityCheckDisabled}
          loading={vm.isQualityCheckLoading}
          onClick={vm.startQualityCheck}
          data-testid="side-panel-start-quality-check"
        >
          Start quality check
        </Button>
        <Button
          variant="link"
          fullWidth
          disabled={vm.isBusy || vm.isSavingDisabled}
          loading={vm.isSaveLoading}
          onClick={vm.saveAndClose}
          data-testid="side-panel-save-and-close"
        >
          Save and close
        </Button>
        {vm.isScanTimedOut && <ScanTimeoutAlert />}
        {vm.scanErrorMessage && (
          <Alert type="error" dismissible onDismiss={vm.dismissScanError}>
            {vm.scanErrorMessage}
          </Alert>
        )}
      </SpaceBetween>

      <PanelDivider />

      <PanelSection title="Recommendation">
        <Box color="text-body-secondary">
          Finish writing your content before you proceed. This will save time during the quality
          check and reduce rescanning costs.
        </Box>
      </PanelSection>
    </SpaceBetween>
  );
});

export { AuthorDraftStatePanel };
